import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #headerLocked = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    const trigger = this.refs.details.querySelector(':scope > summary');
    trigger?.addEventListener('pointerdown', this.#prepareMainDrawer);
    trigger?.addEventListener('keydown', this.#prepareMainDrawer);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    const trigger = this.refs.details.querySelector(':scope > summary');
    trigger?.removeEventListener('pointerdown', this.#prepareMainDrawer);
    trigger?.removeEventListener('keydown', this.#prepareMainDrawer);
  }

  #prepareMainDrawer = (event) => {
    if (this.isOpen) return;
    if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return;
    this.#lockHeaderPosition();
  };

  #lockHeaderPosition() {
    if (this.#headerLocked || window.innerWidth >= 750) return;
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    header.style.setProperty('transition', 'none', 'important');
    void header.offsetHeight;

    const rect = header.getBoundingClientRect();
    const section = header.closest('.header-section');
    // Transparent headers are absolutely positioned (see .header[transparent]
    // in sections/header.liquid) and were never occupying flow height to
    // begin with, so reserving space for them here serves no purpose. On
    // pages where a shopify-section (e.g. the homepage hero) sits
    // immediately after #header-group, that reservation was the direct
    // cause of that content visibly shifting down for as long as the
    // drawer stayed open. Sticky/opaque headers genuinely need this
    // reservation, to stop whatever follows from jumping up into the space
    // the header used to occupy once it goes fixed — that path is untouched.
    if (section instanceof HTMLElement && !header.hasAttribute('transparent')) {
      section.style.setProperty('min-height', `${Math.round(rect.height)}px`);
    }

    header.style.setProperty('position', 'fixed');
    header.style.setProperty('top', `${Math.round(rect.top)}px`);
    header.style.setProperty('left', '0');
    header.style.setProperty('right', '0');
    this.#headerLocked = true;
  }

  #unlockHeaderPosition() {
    if (!this.#headerLocked || window.innerWidth >= 750) return;
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const section = header.closest('.header-section');
    header.style.removeProperty('position');
    header.style.removeProperty('top');
    header.style.removeProperty('left');
    header.style.removeProperty('right');
    if (section instanceof HTMLElement) section.style.removeProperty('min-height');

    this.#headerLocked = false;
    window.setTimeout(() => header.style.removeProperty('transition'), 350);
  }

  #onKeyUp = (event) => {
    if (event.key === 'Escape') this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    if (details === this.refs.details) this.#lockHeaderPosition();
    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (target) this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
          this.#unlockHeaderPosition();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => element instanceof HTMLElement && element.classList.add('details-content--no-animation'));
    setTimeout(() => {
      content.forEach((element) => element instanceof HTMLElement && element.classList.remove('details-content--no-animation'));
    }, 100);
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
